import { Injectable, Injector } from "@angular/core";
import { Router } from "@angular/router";
import { Observable, Subscription } from "rxjs";
import { WebSocketSubject } from "rxjs/webSocket";
import { apiSubscriptionWrapper } from "../../api/api.util";
import { WSUserApi } from "../../api/web-socket/ws-user.api";
import { AuthService } from "../auth/auth.service";
import { BaseService } from "../base.service";
import { ToastService } from "../toast.service";
import { UserService } from "../user/user.service";
import { SocketData } from "./contract/web-socket.contract";

@Injectable({
  providedIn: "root",
})
/**
 * @description This service is used to manage WebSocket User API.
 */
export class WSUserService extends BaseService {
  userWebSocketObservable: Observable<SocketData> | undefined = undefined;
  userWebSocket: WebSocketSubject<SocketData> | undefined = undefined;

  constructor(
    private toastService: ToastService,
    private wsUserApi: WSUserApi,
    private router: Router,
    private injector: Injector,
  ) {
    super();
  }

  /**
   * @description This method is used to register the WebSocket for the user.  If a WebSocket is already registered, it will not create a new one.
   * Should create a new onen on login or on page refresh or on loading a page
   */
  public registerWSUser() {
    if (!this.userWebSocketObservable || !this.userWebSocket || this.userWebSocket.closed) {
      const userService = this.injector.get(UserService);
      if (userService.currentUser) {
        this.userWebSocketObservable = this._registerWSUser();
        // Ensure the WebSocket is opened by subscribing internally
        this.userWebSocketObservable.subscribe({
          next: () => {},
          error: () => {},
          complete: () => {},
        });
      }
    }
  }

  public unregisterWSUser() {
    if (this.userWebSocket) {
      this.userWebSocket.unsubscribe();
      this.userWebSocket = undefined;
    }
    this.userWebSocketObservable = undefined;
  }

  /**
   * @description The calling component can use this method to get the subscription to the user WebSocket observable.
   * Within the component it can then react to the messages received.  Remember to unsubscribe within the component on destroy.
   * @param observer
   * @returns
   */
  public getSubscription(observer: {
    next?: (value: SocketData) => void;
    error?: (error: any) => void;
    complete?: () => void;
  }): Subscription | undefined {
    this.registerWSUser();
    return this.userWebSocketObservable
      ? this.userWebSocketObservable.subscribe(observer)
      : undefined;
  }

  private _registerWSUser(): Observable<SocketData> {
    const authService = this.injector.get(AuthService);

    this.userWebSocket = this.wsUserApi.createWS("register");

    return apiSubscriptionWrapper(this.userWebSocket, {
      authService,
      router: this.router,
      toastService: this.toastService,
      failTitle: "Failed to setup web socket connection",
      onSocketClosed: () => this.unregisterWSUser(), // handle socket being closed by the server
    });
  }
}
